import React from 'react';
import { Link, useLocation } from '@tanstack/react-router';
import { Tooltip } from '@plerionhq/design-system';
import { SidebarItemConfig } from './sidebar-config';

interface SidebarItemProps {
  item: SidebarItemConfig;
  isCollapsed: boolean;
  onClick?: () => void; // Additional onClick handler
  /** When true and collapsed, show icon on top with label below (no tooltip, no badge) */
  showLabelBelow?: boolean;
}

/**
 * Reusable sidebar item component
 * Handles both link and button types with collapsed/expanded states
 */
export const SidebarItem: React.FC<SidebarItemProps> = ({
  item,
  isCollapsed,
  onClick,
  showLabelBelow = false,
}) => {
  const location = useLocation();

  // Check if this route is active
  const isActiveRoute = (path?: string, exact?: boolean) => {
    if (!path) {
      return false;
    }
    const locationPath = location.pathname.replace('/pleri', '');
    if (exact) {
      // Exact match only - no child routes
      return locationPath === path || locationPath === path + '/';
    }
    // Default: match path and child routes
    return locationPath === path || locationPath.startsWith(path + '/');
  };

  const isActive = isActiveRoute(item.to, item.exact);

  // Icon styling - 20px icons; when in mini-with-label, scale is applied via wrapper
  const iconClasses = `h-5 w-5 stroke-[1.8] transition-colors duration-150 ${
    isActive ? 'text-orange-600 dark:text-gray-100' : 'text-gray-900 dark:text-gray-100'
  }`;
  const iconClassesWithScale = `${iconClasses} transition-transform duration-150 group-hover/item:scale-110`;

  // Base classes for both collapsed and expanded states
  const baseClasses =
    'group flex w-full items-center gap-2 rounded-[4.5px] text-left text-sm font-medium text-gray-900 dark:text-gray-100 transition-all duration-150 hover:text-gray-900 dark:hover:text-gray-100';

  const activeClasses = isActive
    ? 'bg-orange-50 dark:bg-gray-700 text-orange-900 dark:text-gray-100 border border-orange-200 dark:border-gray-600'
    : '';

  // Handle custom render
  if (item.type === 'custom' && item.customRender) {
    return <>{item.customRender({ isCollapsed })}</>;
  }

  // Handle divider
  if (item.type === 'divider') {
    return (
      <div className="my-2 border-t border-gray-200 dark:border-gray-700" />
    );
  }

  // Fixed-size icon wrapper; named group/item so only this row's hover triggers (not sidebar root group)
  const miniIconWrapperClasses =
    'flex h-9 w-9 shrink-0 items-center justify-center rounded-lg transition-colors duration-150 group-hover/item:bg-gray-200/80 dark:group-hover/item:bg-gray-700';

  // Collapsed state styling - square hover boxes (fixed size, not full width)
  // Icon is 20px, padding is 10px (p-2.5) on all sides = 40px × 40px square
  const collapsedClasses = `${baseClasses} mx-2 justify-center p-2.5 hover:bg-gray-200 dark:hover:bg-gray-700 hover:shadow-sm w-[40px] h-[40px] ${activeClasses}`;

  // Mini sidebar with label below: named group/item so hover only affects this item, not siblings
  const miniWithLabelClasses = `group/item flex w-full flex-col items-center justify-center gap-1.5 rounded-lg border-0 bg-transparent py-1.5 px-1 min-w-0 transition-colors duration-150 ${activeClasses}`;

  // Expanded state styling - full width but icon has square hover area
  const expandedClasses = `${baseClasses} items-center gap-1 px-2 py-2 hover:bg-gray-200 dark:hover:bg-gray-700 ${activeClasses}`;

  const Icon = item.icon;

  // Handle button type
  if (item.type === 'button') {
    const handleClick = () => {
      if (item.onClick) {
        item.onClick();
      }
      if (onClick) {
        onClick();
      }
    };

    if (isCollapsed && showLabelBelow) {
      return (
        <button
          onClick={handleClick}
          className={miniWithLabelClasses}
          data-testid={item.testId}
          aria-label={item.label}
        >
          {Icon && (
            <span className={miniIconWrapperClasses}>
              <Icon className={iconClassesWithScale} />
            </span>
          )}
          <span className="line-clamp-2 max-w-full text-center text-xs font-medium text-gray-600 dark:text-gray-400">
            {item.label}
          </span>
        </button>
      );
    }

    if (isCollapsed) {
      return (
        <Tooltip label={item.tooltipLabel || item.label} placement="right">
          <button
            onClick={handleClick}
            className={`${collapsedClasses} transition-none`}
            data-testid={item.testId}
            aria-label={item.label}
          >
            {Icon && <Icon className={iconClasses} />}
          </button>
        </Tooltip>
      );
    }

    return (
      <button
        onClick={handleClick}
        className={expandedClasses}
        data-testid={item.testId}
      >
        {Icon && <Icon className={iconClasses} />}
        <span
          className={`whitespace-nowrap transition-all duration-200 ${
            isCollapsed
              ? 'max-w-0 overflow-hidden opacity-0'
              : 'max-w-full opacity-100'
          }`}
          style={{
            transitionDelay: isCollapsed ? '0ms' : '100ms',
          }}
        >
          {item.label}
        </span>
        {!showLabelBelow && item.badge && (
          <span className="ml-auto rounded bg-orange-100 px-1.5 py-0.5 text-xs font-medium text-orange-700 dark:bg-gray-700">
            {item.badge}
          </span>
        )}
      </button>
    );
  }

  // Handle link type
  if (item.type === 'link' && item.to) {
    if (isCollapsed && showLabelBelow) {
      return (
        <Link
          to={item.to}
          className={miniWithLabelClasses}
          data-testid={item.testId}
          onClick={onClick}
        >
          {Icon && (
            <span className={miniIconWrapperClasses}>
              <Icon className={iconClassesWithScale} />
            </span>
          )}
          <span className="line-clamp-2 max-w-full text-center text-xs font-medium text-gray-600 dark:text-gray-400">
            {item.label}
          </span>
        </Link>
      );
    }

    if (isCollapsed) {
      return (
        <Tooltip label={item.tooltipLabel || item.label} placement="right">
          <Link
            to={item.to}
            className={`${collapsedClasses} transition-none`}
            data-testid={item.testId}
            onClick={onClick}
          >
            {Icon && <Icon className={iconClasses} />}
          </Link>
        </Tooltip>
      );
    }

    return (
      <Link
        to={item.to}
        className={expandedClasses}
        data-testid={item.testId}
        onClick={onClick}
      >
        {Icon && <Icon className={iconClasses} />}
        <span
          className={`whitespace-nowrap transition-all duration-200 ${
            isCollapsed
              ? 'max-w-0 overflow-hidden opacity-0'
              : 'max-w-full opacity-100'
          }`}
          style={{
            transitionDelay: isCollapsed ? '0ms' : '100ms',
          }}
        >
          {item.label}
        </span>
        {!showLabelBelow && item.badge && (
          <span className="ml-auto rounded bg-orange-100 px-1.5 py-0.5 text-xs font-medium text-orange-700 dark:bg-gray-700">
            {item.badge}
          </span>
        )}
      </Link>
    );
  }

  return null;
};
